import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

import withAuth from '../utility/withAuth';
import { useAppDispatch } from '../store/hooks';
import { generateJob } from '../store/features/dashboard';
import ThemeChange from '../components/common/ThemeChange';
import DropdownMenu from '../components/common/DropdownMenu';
import ChatWithAI from '../components/employer/Chat/ChatWithAI';
import { addJobInDraft, getUserProfile } from '../store/features/auth';
import { chatBottonIcon, doneTick, logo, backBtn } from '../assets/images/images';
import CreateWithAI from '../components/employer/CreateJob/CreateWithAI';
import { getValue, renderErrorToast, storeValue } from '../utility/helpers';
import { shouldShowUpgradeButton, handleUpgrade as handleUpgradeUtil } from '../utility/tierUtils';
import { ROUTES } from '../resources/routes-constants';
import { EXTERNAL_URLS } from '../resources/url-constants';

const ChatPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userId = getValue('userId');
    const dispatch = useAppDispatch();

    const queryParams = new URLSearchParams(location.search);
    const conversationId = queryParams.get('conversationId');
    const existingJobId = queryParams.get('jobId');
    const [showLoading, setShowLoading] = useState(false);
    const [userDetails, setUserDetails] = useState<any>({});
    // Try to get cached tier data for faster initial render
    const cachedTierData = getValue('tierData') ? JSON.parse(getValue('tierData') || '{}') : {};
    const [assignedTier, setAssignedTier] = useState<any>(cachedTierData);
    const [convoId, setConvoId] = useState<any>(null);
    const rotatingMessages = [
        'Analyzing job postings across your industry peers for accuracy.',
        'Identifying near-peer roles to align your description with market standards.',
        'Leveraging our comprehensive job posting database for real-time insights.',
        'Adapting your job description to match current hiring trends.',
        'Generating a data-driven, market-ready job description just for you.'
    ];
    const [messageIndex, setMessageIndex] = useState(0);
    const [isFading, setIsFading] = useState(false);
    const hasPushedStateRef = useRef(false);

    const handleSaveJob = useCallback(async () => {
        try {
            const payload = {
                conversation_id: conversationId ? conversationId : convoId,
                user: Number(userId)
            };
            await dispatch(addJobInDraft(payload)).unwrap();
        } catch (error) {
            console.info('Error during saving job details:', error);
        }
    }, [conversationId, convoId, userId, dispatch]);

    const navigateTo = useCallback(async (route: any) => {
        // Save conversation before navigating away from chat page
        if (route === '/dashboard' && (conversationId || convoId)) {
            await handleSaveJob();
        }
        navigate(route);
    }, [conversationId, convoId, handleSaveJob, navigate]);

    const generateJobDescription = async () => {
        setShowLoading(true);
        const payload = {
            conversation_id: conversationId ? conversationId : convoId,
            user: Number(userId),
            ...(existingJobId && { job_id: Number(existingJobId) }) // Include job_id if regenerating
        };
        
        try {
            // Always use generateJob endpoint - backend will handle create vs update
            const res = await dispatch(generateJob(payload)).unwrap();
            setShowLoading(false);
            const jobId = existingJobId || res?.data?.job_id;
            if(jobId) {
                storeValue('isGenerate', 'generated');
                navigateTo(`/job-description?jobId=${jobId}`);
            } else {
                renderErrorToast('Job is not generated. Please try again');
            }

        } catch (error) {
            setShowLoading(false);
        }
    };

    const handleUpgrade = () => {
        handleUpgradeUtil(navigate, ROUTES.CONTACT_US_TO_UPGRADE_ROUTE);
    };

    const fetchUserDetails = useCallback(async () => {
        try {
            const companyId = getValue('companyId');
            const payload = {
                companyId,
                loader: false
            };
            const res = await dispatch(getUserProfile(payload)).unwrap();
            storeValue('canCreateJob', res.data.job_left);
            if (res?.data?.tier_assigned) {
                // Cache tier data for faster loading on subsequent visits
                storeValue('tierData', JSON.stringify(res?.data?.tier_assigned));
                setAssignedTier(res?.data?.tier_assigned);
            }
            setUserDetails(res?.data);
        } catch (error) {
            console.info('Error during fetching user details:', error);
        }
    }, [dispatch]);

    // Keep the active conversation id in the URL so a page refresh resumes the same chat
    useEffect(() => {
        // Prefer explicit query param, then newly created convoId, then any server-provided id
        const activeConversationId = conversationId || convoId || userDetails?.conversation_id;
        // If we have an id but it's not reflected in the URL, update the URL (no navigation away)
        if (activeConversationId && conversationId !== String(activeConversationId)) {
            const params = new URLSearchParams(location.search);
            params.set('conversationId', String(activeConversationId));
            if (existingJobId) params.set('jobId', String(existingJobId));
            navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
        }
    }, [convoId, conversationId, userDetails, existingJobId, location.pathname, location.search, navigate]);

    // Handle browser back button to navigate to dashboard and save conversation
    useEffect(() => {
        // Push a state to the history stack only once when component mounts
        if (!hasPushedStateRef.current) {
            window.history.pushState({ page: 'chat' }, '', window.location.href);
            hasPushedStateRef.current = true;
        }

        const handlePopState = () => {
            // When back button is pressed, navigate to dashboard and save conversation
            // Push state again to prevent actual navigation back
            window.history.pushState({ page: 'chat' }, '', window.location.href);
            navigateTo('/dashboard');
        };

        // Add event listener for popstate (browser back button)
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            hasPushedStateRef.current = false;
        };
    }, [navigateTo]);

    useEffect(() => {
        if (!conversationId) {
            fetchUserDetails();
        }
        // fetchUserDetails is stable (only depends on dispatch), only run when conversationId changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    useEffect(() => {
        if (!showLoading) {
            setMessageIndex(0);
            setIsFading(false);
            return;
        }
        let fadeTimeout: any;
        const intervalId = setInterval(() => {
            setIsFading(true);
            fadeTimeout = setTimeout(() => {
                setMessageIndex(prev => (prev + 1) % rotatingMessages.length);
                setIsFading(false);
            }, 300);
        }, 3000);
        return () => {
            clearInterval(intervalId);
            if (fadeTimeout) clearTimeout(fadeTimeout);
        };
    }, [showLoading, rotatingMessages.length]);
    return (
        <>
            {!showLoading && (
                <section className="chat-section">
                    <ThemeChange />
                    <div className='sign-in-header dashboard-header chat-header chat-ai-header'>
                        <div className="container-fluid">
                            <div className="chat-header-inner">
                                <img src={logo} alt="logo" onClick={() => navigateTo('/dashboard')} className='brand-logo' />
                                <div className='welcome-wrap'>
                                    <h4 className='dashboard-subhead text-uppercase'>information gathering</h4>
                                </div>
                                <div className="chat-header-right">
                                    <Button 
                                        variant="primary" 
                                        className={`upgrade-header-btn ${!assignedTier || !shouldShowUpgradeButton(assignedTier) ? 'hidden' : ''}`}
                                        onClick={handleUpgrade}
                                        // Using CSS class for styling
                                    >
                                        <span className="d-none d-md-inline">Contact us to upgrade</span>
                                        <span className="d-md-none">Upgrade</span>
                                    </Button>
                                    <DropdownMenu assignedTier={assignedTier}/>
                                </div>

                                {/* sign out confirmation alert */}
                                <div className="sign-out-confirmation d-none">
                                    <span className='title'>Sign Out of the platform?</span>
                                    <div className='btn-wrap'>
                                        <span className='btn-outline'>stay</span>
                                        <Button className='btn-primary'>yes</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="chat-back-button-container">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Back</Tooltip>}>
                            <div className="back-btn chat-back-btn" onClick={() => navigateTo('/dashboard')}>
                                <Image
                                    src={backBtn}
                                    height={25}
                                    width={25}
                                    alt="Back"
                                    className="back-btn-arrow"
                                />
                            </div>
                        </OverlayTrigger>
                    </div>

                    {/* AI Chat */}
                    {conversationId ?
                        <ChatWithAI
                            conversationId={conversationId}
                            generateJobDescription={generateJobDescription}
                            handleSaveJob={handleSaveJob}
                        /> :
                        <CreateWithAI
                            conversationId={userDetails?.conversation_id}
                            userDetails={userDetails}
                            setConvoId={setConvoId}
                            convoId={convoId}
                            generateJobDescription={generateJobDescription}
                            handleAutoSave={handleSaveJob}
                        />
                    }
                </section>
            )}

            {showLoading && (
                <section className="chat-section chat-dashboard-loading-sec">
                    <div className="video-container">
                        <video
                            className="footer-video"
                            controlsList="nodownload"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                        >
                            <source src={EXTERNAL_URLS.VIDEOS.LANDING_PAGE} type="video/mp4" />
                        </video>
                        <div className="video-overlay"></div>

                    </div>
                    <div className="container">
                        <div className="chat-dashboard-loading">
                            <div className="loader-wrap">
                                <div className="toggle">
                                    <div className="loader"></div>
                                </div>
                            </div>
                            <p
                                className="chat-dashboard-loading-des"
                                style={{ opacity: isFading ? 0 : 1, transition: 'opacity 300ms ease' }}
                            >
                                {rotatingMessages[messageIndex]}<span className="green-dot">...</span>
                            </p>
                        </div>
                        <div className="chat-dashboard-loading done-status d-none">
                            <div className="done-wrap">
                                <p className="chat-dashboard-loading-des">Done</p>
                                <span className="green-tick"><img src={doneTick} alt="tick" /></span>
                            </div>
                        </div>
                    </div>
                    <img src={chatBottonIcon} alt="brand" className="chat-bottom-img" />
                </section>
            )}
        </>
    );
};

export default withAuth(ChatPage);
